import * as React from 'react';
import type {
  HealthMetricValueV3 as HealthMetricValue,
  HealthMetricInput,
} from '@leagueplatform/dashboard-api';
import type { DefaultValues } from '#components/add-edit-data/add-edit-data-modal.tsx';

export type MetricWithConfigId = HealthMetricValue & {
  metricConfigId: string;
};

type FormattedMetricGroup = {
  metrics: MetricWithConfigId[];
  healthMetricInputs: HealthMetricInput[];
  defaultValuesForModal: DefaultValues[];
};

type GroupedAndFormattedMetrics = Record<string, FormattedMetricGroup>;

type UseGroupAndFormatMetricsParams = {
  metrics: MetricWithConfigId[];
};

type UseGroupAndFormatMetricsResult = {
  groupedMetrics: GroupedAndFormattedMetrics;
};

/**
 * Hook that groups metrics by their metricGroupingKey (or id if not present)
 * and formats them in a single pass.
 * Metrics without a grouping key are treated as individual groups (group of 1).
 * @param metrics - Array of metrics to group and format
 * @returns Object containing grouped metrics
 */
export function useGroupAndFormatMetrics({
  metrics,
}: UseGroupAndFormatMetricsParams): UseGroupAndFormatMetricsResult {
  return React.useMemo(() => {
    const result = metrics.reduce<GroupedAndFormattedMetrics>(
      (acc, dataMetric) => {
        // Use metricGroupingKey if available, otherwise use the metric's id as a unique key
        const groupingKey = dataMetric.metricGroupingKey || dataMetric.id;

        // Initialize group if it doesn't exist
        if (!acc[groupingKey]) {
          acc[groupingKey] = {
            metrics: [],
            healthMetricInputs: [],
            defaultValuesForModal: [],
          };
        }

        // Add metric to group
        acc[groupingKey].metrics.push(dataMetric);

        // Collect healthMetricInputs
        if (dataMetric.editInputConfig.data.healthMetricInputs) {
          acc[groupingKey].healthMetricInputs.push(
            ...dataMetric.editInputConfig.data.healthMetricInputs,
          );
        }

        // Collect default values
        acc[groupingKey].defaultValuesForModal.push({
          observationId: dataMetric.id,
          value: dataMetric.value,
          label: dataMetric.label,
        });

        return acc;
      },
      {},
    );

    return {
      groupedMetrics: result,
    };
  }, [metrics]);
}
